import { useMemo } from "react";

import { getSchemaHookRegistry } from "./schemaRegistry";

/**
 * Generic hook to get row click handler for any integration module.
 * Resolves to integration-specific hook based on integrationName and moduleName.
 * 
 * @param integrationName - The integration name (e.g., "werkzoeken")
 * @param moduleName - The module name (e.g., "job-sync")
 * @param clientId - The client ID
 * @returns Row click handler function, or undefined if not available
 */
export function useSchemaRowClick(
  integrationName: string | undefined,
  moduleName: string,
  clientId: string | undefined
): ((row: unknown) => void) | undefined {
  // Get module registry and resolve hook
  const moduleRegistry = getSchemaHookRegistry(moduleName);
  const registeredHook = moduleRegistry?.getRowClickHook?.(integrationName ?? "");
  
  // Create a no-op hook that returns undefined if no registered hook exists
  // This ensures hooks are always called unconditionally
  const noOpHook = () => undefined;
  const hookToCall = registeredHook ?? noOpHook;
  
  // Call the hook unconditionally (satisfies React hooks rules)
  const rowClickHandler = hookToCall({ clientId });
  
  return useMemo(() => rowClickHandler, [rowClickHandler]);
}
